/**
 * SportsServicesSection - Refactored
 * Main container that dynamically loads the appropriate section based on subcategory
 * Reduced from ~2400 lines to ~150 lines
 */
import React, { Suspense, lazy } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2 } from 'lucide-react';

// Lazy load section components for code splitting
const YogaWellnessSection = lazy(() => import('./sections/YogaWellnessSection'));
const BoardGamesSection = lazy(() => import('./sections/BoardGamesSection'));
const CreativeHobbiesSection = lazy(() => import('./sections/CreativeHobbiesSection'));
const FishingHuntingSection = lazy(() => import('./sections/FishingHuntingSection'));
const OutdoorCampingSection = lazy(() => import('./sections/OutdoorCampingSection'));
const CyclingSection = lazy(() => import('./sections/CyclingSection'));
const SportsEquipmentSection = lazy(() => import('./sections/SportsEquipmentSection'));

// Loading fallback component
const LoadingFallback = ({ title = 'Sports Services' }) => (
  <Card className="border-gray-200">
    <CardContent className="p-6 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      <span className="ml-2 text-gray-600">Loading {title}...</span>
    </CardContent>
  </Card>
);

// Map subcategories to their section components
const SECTION_MAP = {
  'yoga-wellness': YogaWellnessSection,
  'board-games-puzzles': BoardGamesSection,
  'creative-hobbies': CreativeHobbiesSection,
  'fishing-hunting': FishingHuntingSection,
  'outdoor-camping': OutdoorCampingSection,
  'cycling-equipment': CyclingSection,
  'sports-equipment': SportsEquipmentSection,
  'fitness-gym': SportsEquipmentSection, // Uses same component with different subcategory
};

// Display titles for loading state
const DISPLAY_TITLES = {
  'yoga-wellness': 'Yoga & Wellness',
  'board-games-puzzles': 'Board Games',
  'creative-hobbies': 'Creative Hobbies',
  'fishing-hunting': 'Fishing & Hunting',
  'outdoor-camping': 'Outdoor & Camping',
  'cycling-equipment': 'Cycling Services',
  'sports-equipment': 'Sports Equipment Services',
  'fitness-gym': 'Fitness & Gym Services',
};

/**
 * SportsServicesSection
 * Displays service cards for sports & fitness categories
 * Uses dynamic imports for code splitting and better performance
 */
const SportsServicesSection = ({ 
  subcategory,
  productContext = null,
  userLocation = null,
  maxServices = 4,
  viewMode = 'grid',
  showDualCard = true,
  showMobilePill = true,
  showSafetyCard = false,
  showBundles = true,
  showSeasonalInfo = true,
  postCheckoutProduct = null,
  country = 'SL',
}) => {
  // Get the appropriate section component
  const SectionComponent = SECTION_MAP[subcategory] || SportsEquipmentSection;
  const displayTitle = DISPLAY_TITLES[subcategory] || 'Sports Services';

  // Common props to pass to all section components
  const commonProps = {
    country,
    maxServices,
    showMobilePill,
    showBundles,
    postCheckoutProduct,
  };

  // Additional props for specific sections
  const specificProps = {
    ...(subcategory === 'sports-equipment' || subcategory === 'fitness-gym' ? {
      subcategory,
      viewMode,
      showDualCard,
      showSafetyCard,
    } : {}),
    ...(subcategory === 'outdoor-camping' ? {
      showSeasonalInfo,
    } : {}),
    ...(subcategory === 'cycling-equipment' ? {
      productContext,
    } : {}),
  };

  return (
    <Suspense fallback={<LoadingFallback title={displayTitle} />}>
      <SectionComponent {...commonProps} {...specificProps} />
    </Suspense>
  );
};

export default SportsServicesSection;
